import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './PricingPage.css'; // For specific styling

// Placeholder for icons
const PlanIcon = ({ iconClass, cardType }) => <i className={`${iconClass} plan-icon ${cardType}-icon mb-3`}></i>;

const pricingPlans = [
  {
    id: 'basic',
    title: 'Basic Plan',
    price: '₹ 1200',
    priceFrequency: '/visit',
    description: 'Perfect for small homes & one-time treatments.',
    features: [
      '1 Comprehensive Visit',
      'Covers Common Pests (Ants, Spiders)',
      'Exterior Perimeter Spray',
      '30-Day Guarantee'
    ],
    iconClass: 'bi bi-shield-house', // House icon representing basic home service
    buttonText: 'Get Started',
    highlight: false,
    bgColorClass: 'bg-light',
    cardStyle: {
      border: '1px solid #e9ecef',
      borderRadius: '12px',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
      }
    } // Bootstrap bg class or custom
  },
  {
    id: 'standard',
    title: 'Standard Plan',
    price: '₹ 1500',
    priceFrequency: '/month',
    description: 'Perfect for apartments & regular pest control.',
    features: [
      'Quarterly Visits (4/year)',
      'Covers Most Common Pests',
      'Interior & Exterior Treatment',
      'Rodent Monitoring Included',
      '60-Day Guarantee'
    ],
    iconClass: 'bi bi-shield-check',
    buttonText: 'Choose Plan',
    highlight: true, // To make this card stand out
    bgColorClass: 'bg-white' 
  },
  {
    id: 'premium',
    title: 'Premium Plan',
    price: '₹ 2000',
    priceFrequency: '/month',
    description: 'Best for large homes, villas, or commercial properties.',
    features: [
      'Monthly Visits (12/year)',
      'Comprehensive Pest Coverage',
      'Termite & Bed Bug Inspection',
      'Emergency Support (24/7)',
    ],
    iconClass: 'bi bi-shield-shaded',
    buttonText: 'Select Premium',
    highlight: false,
    bgColorClass: 'bg-dark text-white' // Example of a different theme
  },
];

const whyInvestData = {
  title: "Why Invest in Professional Pest Control?",
  benefits: [
    { id: 1, text: "Protect your health by eliminating disease-carrying pests.", icon: "bi bi-heart-pulse-fill" },
    { id: 2, text: "Prevent costly property damage from termites, rodents, etc.", icon: "bi bi-house-heart-fill" },
    { id: 3, text: "Ensure hygiene in your living and working spaces.", icon: "bi bi-check2-circle" },
    { id: 4, text: "Save money in the long run by avoiding major infestations.", icon: "bi bi-piggy-bank-fill" },
    { id: 5, text: "Gain peace of mind knowing your property is protected.", icon: "bi bi-emoji-smile-fill" }
  ],
  stats: [
    { id: 1, value: "500+", label: "Happy Customers", icon: "bi bi-people-fill" },
    { id: 2, value: "98%", label: "Success Rate", icon: "bi bi-graph-up-arrow" },
    { id: 3, value: "10+", label: "Years Experience", icon: "bi bi-award-fill" }
  ]
};

const PricingPage = () => {
  const cardVariants = {
    initial: { y: 50, opacity: 0 },
    animate: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        bounce: 0.3,
        duration: 0.8,
        delay: i * 0.15
      }
    })
  };

  const sectionVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.2 } }
  };

  return (
    <div className="pricing-page-container py-5">
      <div className="container">
        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-5 pb-3"
        >
          <h1 className="display-4 fw-bold page-title-pricing">Pick Your Plan</h1>
          <p className="lead text-muted">Affordable and effective pest control solutions tailored to your needs.</p>
        </motion.div>

        {/* Pricing Cards Section */}
        <div className="row justify-content-center g-4 g-lg-5 mb-5 pb-4">
          {pricingPlans.map((plan, index) => (
            <motion.div 
              className={`col-lg-4 col-md-6 d-flex align-items-stretch pricing-card-col ${plan.id}-plan-col`}
              key={plan.id}
              custom={index}
              initial="initial"
              animate="animate"
              variants={cardVariants}
            >
              <motion.div 
                className={`card pricing-card h-100 shadow ${plan.id}-card ${plan.highlight ? 'highlighted-plan' : ''} ${plan.bgColorClass}`}
                whileHover={{ y: -10, boxShadow: "0px 15px 25px rgba(0,0,0,0.15)" }}
              >
                <div className={`card-header ${plan.id}-header text-center py-4`}>
                  <PlanIcon iconClass={plan.iconClass} cardType={plan.id} />
                  <h3 className="card-title plan-title fw-bold mb-0">{plan.title}</h3>
                </div>
                <div className="card-body d-flex flex-column text-center p-4">
                  <p className={`price-display fw-bolder my-3 ${plan.id}-price`}>
                    {plan.price}<small className="text-muted price-frequency">{plan.priceFrequency}</small>
                  </p>
                  <p className={`plan-description mb-4 ${plan.id === 'premium' ? 'text-white' : 'text-muted'}`}>{plan.description}</p>
                  <ul className="list-unstyled plan-features mb-4 flex-grow-1">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="mb-2 d-flex align-items-center justify-content-center">
                        <i className="bi bi-check-lg text-success me-2"></i> {feature}
                      </li>
                    ))}
                  </ul>
                  <button className={`btn btn-lg w-100 fw-medium ${plan.highlight ? 'btn-danger' : 'btn-outline-danger'} ${plan.id}-button`}>
                    View Plan
                  </button>
                </div>
                {plan.highlight && <div className="highlight-banner">Most Popular</div>}
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Why Invest in Pest Control Section */}
        <motion.section 
          className="why-invest-section py-5 mt-4 bg-light rounded"
          variants={sectionVariant}
          initial="hidden"
          animate="visible"
        >
          <div className="container">
            <h2 className="text-center mb-5 section-title-pricing">{whyInvestData.title}</h2>
            <div className="row g-4 mb-5">
              {whyInvestData.benefits.map((benefit, index) => (
                <motion.div 
                  className="col-md-6 col-lg-4 d-flex"
                  key={benefit.id}
                  custom={index}
                  initial="initial"
                  animate="animate"
                  variants={cardVariants} // Re-use cardVariants for staggered item animation
                >
                  <div className="benefit-item p-3 text-center">
                    <i className={`${benefit.icon} benefit-icon display-4 mb-3 d-block`}></i>
                    <p className="text-muted">{benefit.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="row text-center g-4 justify-content-center">
              {whyInvestData.stats.map((stat, index) => (
                <motion.div 
                  className="col-md-4"
                  key={stat.id}
                  custom={index + whyInvestData.benefits.length} // Continue stagger index
                  initial="initial"
                  animate="animate"
                  variants={cardVariants}
                >
                  <div className="stat-item p-3">
                    <i className={`${stat.icon} stat-icon display-3 mb-2 d-block text-danger`}></i>
                    <p className="stat-value display-5 fw-bold text-dark mb-1">{stat.value}</p>
                    <p className="stat-label text-muted fs-5">{stat.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

      </div>
    </div>
  );
};

export default PricingPage;
